import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function AboutPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setMessage('感谢您的留言！');
      setName('');
      setEmail('');
      setContent('');
      return;
    }
    if (content.length > 500) {
      setStatus('error');
      setMessage('留言内容不能超过500字');
      return;
    }
    setStatus('loading');
    setMessage('');
    try {
      const resp = await fetch('https://readdy.ai/api/form/d9slent8o1vn02ekuhd0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          content: formData.get('content') as string,
        }).toString(),
      });
      const text = await resp.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch {}
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || '';
      if (resp.ok && code === 'OK') {
        setStatus('success');
        setMessage('感谢您的留言！我们会尽快回复您。');
        setName('');
        setEmail('');
        setContent('');
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      // form submission failed, handled below
      setStatus('error');
      setMessage('网络错误，请稍后重试。');
    }
  }, [content]);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <section className="w-full px-6 md:px-10 pt-24 pb-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>

            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于孟若羽官方粉丝站
            </h1>
            <p className="text-foreground-600 text-base max-w-3xl leading-relaxed">
              我们是一个由热爱孟若羽的粉丝自发组织的资讯聚合平台，致力于为所有"羽毛"提供最新、最全面的孟若羽相关资讯。
            </p>
          </div>
        </section>

        <section id="brand" className="w-full px-6 md:px-10 py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative w-full h-80 md:h-96 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20Chinese%20actress%20in%20warm%20studio%20portrait%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20confident%20smile%2C%20modern%20editorial%20fashion%20photography%2C%20minimal%20aesthetic%2C%20professional%20headshot%20style&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="孟若羽"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                  品牌故事
                </h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed text-sm">
                  <p>
                    孟若羽官方粉丝站成立于2025年，由一群热爱孟若羽的忠实粉丝共同创建。我们希望通过这个平台，将孟若羽的演艺动态、作品资讯和幕后故事完整地呈现给每一位关注她的人。
                  </p>
                  <p>
                    孟若羽是一位用真诚演绎每一个角色的实力派演员。从中央戏剧学院毕业后，她凭借扎实的表演功底和对角色的深刻理解，在多部影视作品中展现了令人印象深刻的表演。无论是在《春风十里》中勇敢追梦的林小满，还是在《城市之光》中内敛克制的苏念，她都能将角色的灵魂演绎得淋漓尽致。
                  </p>
                  <p>
                    作为粉丝站，我们秉持"真实、全面、及时"的原则，用心收集和整理与孟若羽相关的一切资讯。我们不只是搬运信息，更希望通过精心的编辑和呈现，让每一位访客都能更深入地了解这位优秀演员的成长历程和艺术追求。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="reputation" className="w-full px-6 md:px-10 py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                口碑与评价
              </h2>
              <p className="text-foreground-600 text-sm max-w-lg mx-auto">
                来自媒体、业内人士和粉丝的真实评价
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-background-100 rounded-lg p-8">
                <div className="flex items-center gap-1 mb-4 text-accent-500">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed mb-6">
                  "孟若羽是近年来最令人惊喜的新生代演员之一。她在《春风十里》中的表演自然流畅，情感表达细腻准确，展现出了超乎年龄的表演成熟度。"
                </p>
                <div>
                  <p className="font-medium text-foreground-950 text-sm">《影视观察》杂志</p>
                  <p className="text-foreground-500 text-xs mt-1">2025年3月刊</p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-8">
                <div className="flex items-center gap-1 mb-4 text-accent-500">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed mb-6">
                  "与若羽合作是一种享受。她对角色有着独特的理解，每次拍摄都会带来惊喜。她不是简单地执行导演的指示，而是真正地与角色融为一体。"
                </p>
                <div>
                  <p className="font-medium text-foreground-950 text-sm">《春风十里》导演</p>
                  <p className="text-foreground-500 text-xs mt-1">幕后采访</p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-8">
                <div className="flex items-center gap-1 mb-4 text-accent-500">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed mb-6">
                  "关注若羽两年多了，看着她一步步成长。她不仅是演技在进步，为人也一直谦逊真诚。每次见面会都能感受到她对粉丝的真心。"
                </p>
                <div>
                  <p className="font-medium text-foreground-950 text-sm">资深粉丝 @羽毛飘飘</p>
                  <p className="text-foreground-500 text-xs mt-1">粉丝社群</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="font-heading text-3xl md:text-4xl font-bold text-primary-600 mb-2">500万+</div>
                <p className="text-foreground-600 text-sm">社交媒体粉丝</p>
              </div>
              <div>
                <div className="font-heading text-3xl md:text-4xl font-bold text-accent-600 mb-2">30亿+</div>
                <p className="text-foreground-600 text-sm">剧集总播放量</p>
              </div>
              <div>
                <div className="font-heading text-3xl md:text-4xl font-bold text-primary-600 mb-2">15+</div>
                <p className="text-foreground-600 text-sm">影视作品</p>
              </div>
              <div>
                <div className="font-heading text-3xl md:text-4xl font-bold text-accent-600 mb-2">8万+</div>
                <p className="text-foreground-600 text-sm">粉丝社群成员</p>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="w-full px-6 md:px-10 py-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-600 text-sm">
                如果您有任何建议、合作意向或想加入粉丝社群，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                <p className="text-foreground-600 text-sm">contact@mengruoyu.com</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-weibo-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">官方微博</h3>
                <p className="text-foreground-600 text-sm">@孟若羽官方粉丝站</p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-group-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">粉丝社群</h3>
                <p className="text-foreground-600 text-sm">QQ群：888888888</p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                    您的姓名 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-foreground-950 placeholder:text-foreground-400 text-sm outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                    电子邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-foreground-950 placeholder:text-foreground-400 text-sm outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-content" className="block text-sm font-medium text-foreground-700 mb-2">
                  留言内容 <span className="text-primary-600">*</span>
                  <span className="text-foreground-400 font-normal ml-2">（最多500字）</span>
                </label>
                <textarea
                  id="contact-content"
                  name="content"
                  required
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-foreground-950 placeholder:text-foreground-400 text-sm outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您想说的话..."
                ></textarea>
                <p className="text-right text-xs text-foreground-400 mt-1">{content.length}/500</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="visually-hidden"
              />

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap disabled:opacity-50"
              >
                {status === 'loading' ? '提交中...' : '发送留言'}
              </button>

              {message && (
                <p
                  className={`text-sm text-center ${
                    status === 'success' ? 'text-green-600' : 'text-primary-600'
                  }`}
                >
                  {message}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}